import { useCallback, useEffect, useState, type SyntheticEvent } from "react";
import {
  createImUser,
  listImUsers,
  type Application,
  type ImUserDraft,
  type ImUserListItem,
  type PageResult,
} from "../api";
import { Button } from "./ui/Button";
import { Dialog } from "./ui/Dialog";
import { Icon } from "./ui/Icon";
import {
  CopyText,
  ManagementHeading,
  PageState,
  PaginationControls,
  StatusTag,
  TimeValue,
  UserIdentity,
} from "./ManagementCommon";

export function UserManagementView({ application, applications, onSelect, onNotice, onError }: {
  readonly application: Application | null;
  readonly applications: readonly Application[];
  readonly onSelect: (application: Application) => void;
  readonly onNotice: (message: string) => void;
  readonly onError: (message: string) => void;
}) {
  const [draftKeyword, setDraftKeyword] = useState("");
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [result, setResult] = useState<PageResult<ImUserListItem> | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setDraftKeyword(""); setKeyword(""); setStatus(""); setPageNo(1); setResult(null); setLoadError(null);
  }, [application?.appId]);

  const load = useCallback(() => { setReloadKey((current) => current + 1); }, []);
  useEffect(() => {
    if (application === null) return;
    let active = true;
    setLoading(true); setLoadError(null); setResult(null);
    void listImUsers(application.appId, { pageNo, pageSize: 20, keyword, status })
      .then((value) => { if (active) setResult(value); })
      .catch((cause: unknown) => { if (active) setLoadError(errorMessage(cause)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [application?.appId, keyword, pageNo, reloadKey, status]);

  const canCreate = application?.permissions.includes("im.user.create") === true;
  return (
    <div className="management-page">
      <ManagementHeading
        title="用户管理"
        description="查看和创建当前应用租户内的 IM 用户，数据不会跨应用共享。"
        applications={applications}
        selected={application}
        onSelect={onSelect}
        action={canCreate ? <Button leadingIcon="plus" variant="primary" onClick={() => { setCreateOpen(true); }}>创建 IM 用户</Button> : undefined}
      />
      {application === null ? <NoApplication /> : (
        <section className="data-card management-card">
          <header className="data-card-header management-toolbar">
            <div><h3>用户列表</h3><p>{result === null ? "应用级用户目录" : `共 ${result.total} 个用户`}</p></div>
            <form className="table-toolbar" onSubmit={(event) => { event.preventDefault(); setPageNo(1); setKeyword(draftKeyword.trim()); }}>
              <label className="search-control"><Icon name="search" size={17} /><span className="visually-hidden">搜索用户</span><input maxLength={64} placeholder="用户 ID 或昵称" value={draftKeyword} onChange={(event) => { setDraftKeyword(event.currentTarget.value); }} /></label>
              <label className="select-control"><span className="visually-hidden">用户状态</span><select value={status} onChange={(event) => { setStatus(event.currentTarget.value); setPageNo(1); }}><option value="">全部状态</option><option value="1">正常</option><option value="2">已停用</option><option value="0">不可用</option></select><Icon name="chevron-down" size={15} /></label>
              <Button size="small" type="submit">搜索</Button>
              <Button size="small" variant="ghost" type="button" onClick={() => { setDraftKeyword(""); setKeyword(""); setStatus(""); setPageNo(1); }}>重置</Button>
              <Button size="small" variant="ghost" type="button" onClick={load}>刷新</Button>
            </form>
          </header>
          <PageState loading={loading} error={loadError} empty={result?.items.length === 0} filtered={keyword.length > 0 || status.length > 0} noun="用户" onRetry={load}>
            <div className="table-container"><table className="management-table user-table"><thead><tr><th>用户</th><th>用户 ID</th><th>状态</th><th>来源</th><th>连接池</th><th>创建时间</th><th>最近活跃</th></tr></thead><tbody>{result?.items.map((user) => <tr key={user.userId}><td><UserIdentity userId={user.userId} displayName={user.displayName} avatarUrl={user.avatarUrl} /></td><td><CopyText label="用户 ID" value={user.userId} /></td><td><StatusTag status={user.status} /></td><td><span className="management-tag tag-muted">{userSourceLabel(user.source)}</span></td><td><code>{user.homePool}</code></td><td><TimeValue value={user.createdAt} /></td><td><TimeValue value={user.lastActiveAt} /></td></tr>)}</tbody></table></div>
          </PageState>
          {result === null ? null : <PaginationControls {...result} onChange={setPageNo} />}
        </section>
      )}
      {createOpen && application !== null ? <CreateUserDialog application={application} onClose={() => { setCreateOpen(false); }} onCreated={(user) => { setCreateOpen(false); setDraftKeyword(user.userId); setKeyword(user.userId); setStatus(""); setPageNo(1); onNotice(`用户 ${user.userId} 已创建`); load(); }} onError={onError} /> : null}
    </div>
  );
}

function CreateUserDialog({ application, onClose, onCreated, onError }: {
  readonly application: Application;
  readonly onClose: () => void;
  readonly onCreated: (user: ImUserListItem) => void;
  readonly onError: (message: string) => void;
}) {
  const [draft, setDraft] = useState<ImUserDraft>({ userId: "", displayName: "", avatarUrl: null });
  const [busy, setBusy] = useState(false);
  async function submit(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault(); setBusy(true);
    try { const result = await createImUser(application.appId, draft); onCreated(result.user); }
    catch (cause) { onError(errorMessage(cause)); }
    finally { setBusy(false); }
  }
  return <Dialog busy={busy} description="用户创建后即可由业务服务端签发 UserSig 并登录 IM。" eyebrow={application.name} title="创建 IM 用户" onClose={onClose}><form className="dialog-form" onSubmit={(event) => { void submit(event); }}><div className="form-grid"><label className="field field-full"><span>用户 ID <em>*</em></span><input required maxLength={128} pattern="[A-Za-z0-9][A-Za-z0-9._:@-]*" placeholder="例如 user_10001" value={draft.userId} onChange={(event) => { setDraft({ ...draft, userId: event.currentTarget.value }); }} /><small>应用内唯一，创建后不可修改。</small></label><label className="field field-full"><span>用户昵称</span><input maxLength={256} placeholder="未填写时使用用户 ID" value={draft.displayName ?? ""} onChange={(event) => { setDraft({ ...draft, displayName: event.currentTarget.value }); }} /></label><label className="field field-full"><span>头像 URL</span><input type="url" maxLength={2048} placeholder="https://example.com/avatar.png" value={draft.avatarUrl ?? ""} onChange={(event) => { setDraft({ ...draft, avatarUrl: event.currentTarget.value || null }); }} /><small>仅支持 HTTPS；开放平台不会上传或代理头像。</small></label></div><footer className="dialog-footer"><Button disabled={busy} type="button" onClick={onClose}>取消</Button><Button loading={busy} type="submit" variant="primary">创建用户</Button></footer></form></Dialog>;
}

function NoApplication() {
  return <div className="empty-page"><strong>请先创建应用</strong><p>运营管理数据必须归属于一个应用。</p></div>;
}

function errorMessage(cause: unknown): string { return cause instanceof Error ? cause.message : String(cause); }

function userSourceLabel(source: ImUserListItem["source"]): string {
  if (source === "SERVER_API") return "服务端创建";
  if (source === "CLIENT_SSO") return "客户端注册";
  return "历史来源未知";
}
